import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Form,
  Spinner,
  Badge,
  Alert,
  Pagination
} from 'react-bootstrap';
import { useGlobal } from '../../Context/GlobalContext';
import { useWorkspace } from '../../Context/WorkspaceContext';
import { useLanguage } from '../../Context/LanguageContext';
import {
  FiMessageSquare,
  FiFilter,
  FiGlobe,
  FiMapPin,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiUser,
  FiInfo,
  FiArrowUp,
  FiArrowDown,
  FiEyeOff,
  FiThumbsUp,
  FiThumbsDown,
  FiEdit,
  FiChevronDown,
  FiChevronUp,
  FiInstagram,
  FiSend,
  FiTrash2,
  FiMail,
  FiPhone
} from 'react-icons/fi';
import HumanSupportTour from '../../Components/HumanSupportTour';
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  toolbarPlugin,
  listsPlugin,
  quotePlugin,
  headingsPlugin,
  thematicBreakPlugin,
  codeBlockPlugin,
  markdownShortcutPlugin,
  BlockTypeSelect,
  ListsToggle,
  Separator
} from '@mdxeditor/editor';
import '@mdxeditor/editor/style.css';
import PageHeader from '../../Components/PageHeader';
import './agent-chats.css';
import { marked } from 'marked';
import { DateTimePicker } from '@mantine/dates';
import { rem } from '@mantine/core';
import AddQAModal from './../Training/Modals/AddQAModal';
import CompactUpgradePrompt from '../../Components/CompactUpgradePrompt';
import ExportDropdown from '../../Components/ExportDropdown';
import CustomPopup from '../../Components/CustomPopup';
import { SiGoogle, SiOpenai } from 'react-icons/si';
import { useAIModels } from '../../Context/AIModelsContext';
import { FiCpu } from 'react-icons/fi';
import {
  BsWhatsapp,
  BsSlack,
  BsTelegram
} from 'react-icons/bs';
import { FaFacebookMessenger } from 'react-icons/fa';
import { AgentChatsSkeleton } from '../../Components/SkeletonLoaders';
import LinkButton from '../../Components/Buttons/LinkButton';
import OutlinedSecondaryButton from '../../Components/Buttons/OutlinedSecondaryButton';
import PrimaryButton from '../../Components/Buttons/PrimaryButton';
import OutlinedDangerButton from '../../Components/Buttons/OutlinedDangerButton';
// Remove mock data import

// Loading message component
const LoadingMessage = ({ message }) => (
  <div className='loading-container'>
    <Spinner
      animation='border'
      className='loading-spinner'
      role='status'
    >
      <span className='visually-hidden'>{message}</span>
    </Spinner>
    <h5 className='loading-text'>{message}</h5>
  </div>
);

// Compact MDXEditor for human support input
const CompactMDXEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  return (
    <div style={{
      border: '2px solid #f0f0f0',
      borderRadius: '12px',
      overflow: 'hidden',
      background: '#fff',
      transition: 'all 0.3s ease',
      minWidth: 0,
      flexShrink: 1,
    }}>
      <style>{`
        .human-support-editor .mdxeditor {
          font-size: 14px;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          --mdxeditor-color: #2c1810;
          --mdxeditor-border: #f0f0f0;
          min-width: 0;
          max-width: 100%;
        }
        .human-support-editor .mdxeditor-toolbar {
          border-bottom: 1px solid #f0f0f0;
          background-color: #fafbfc;
          padding: 8px 12px;
          min-height: 40px;
          display: flex;
          align-items: center;
          gap: 4px;
          min-width: 0;
          flex-wrap: wrap;
        }
        .human-support-editor .mdxeditor-root-contenteditable {
          min-height: 120px;
          max-height: 300px;
          overflow-y: auto;
          font-size: 14px;
          line-height: 1.6;
          font-family: "Inter", "Roboto", "Helvetica", "Arial", sans-serif;
          word-break: break-word;
          overflow-wrap: break-word;
          max-width: 100%;
        }
          @media (max-width: 768px) {
            .human-support-editor .mdxeditor-root-contenteditable {
              min-height: 80px;
              max-height: 200px;
              font-size: 16px;
            }
          }
        .human-support-editor .mdxeditor-toolbar button {
          border-radius: 6px;
          padding: 6px 10px;
          transition: all 0.2s ease;
          font-size: 13px;
          min-width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .human-support-editor .mdxeditor-toolbar button:hover {
          background-color: rgba(85, 41, 127, 0.1);
          transform: translateY(-1px);
        }
        .human-support-editor .mdxeditor-toolbar select {
          font-size: 13px;
          padding: 4px 8px;
          height: 32px;
          border: 1px solid #ddd;
          border-radius: 6px;
          background: white;
        }
        @media (max-width: 768px) {
          .human-support-editor .mdxeditor-toolbar {
            padding: 6px 8px;
            min-height: 36px;
            gap: 3px;
          }
          .human-support-editor .mdxeditor-toolbar button {
            border-radius: 5px;
            padding: 4px 7px;
            font-size: 11px;
            min-width: 28px;
            height: 28px;
          }
          .human-support-editor .mdxeditor-toolbar button svg {
            width: 14px;
            height: 14px;
          }
          .human-support-editor .mdxeditor-toolbar select {
            font-size: 11px;
            padding: 3px 6px;
            height: 28px;
            border-radius: 5px;
          }
          .human-support-editor .mdxeditor-toolbar [role="separator"] {
            height: 24px;
            margin: 0 3px;
          }
        }
      `}</style>
      <div className="human-support-editor">
        <MDXEditor
          ref={editorRef}
          markdown={value || ''}
          onChange={onChange}
          placeholder={placeholder}
          plugins={[
            headingsPlugin({ allowedHeadingLevels: [1, 2, 3] }),
            listsPlugin(),
            quotePlugin(),
            thematicBreakPlugin(),
            codeBlockPlugin({ defaultCodeBlockLanguage: 'text' }),
            markdownShortcutPlugin(),
            toolbarPlugin({
              toolbarContents: () => (
                <>
                  <UndoRedo />
                  <Separator />
                  <BoldItalicUnderlineToggles />
                  <Separator />
                  <BlockTypeSelect />
                  <Separator />
                  <ListsToggle />
                </>
              )
            })
          ]}
        />
      </div>
    </div>
  );
};

// Chat message component
const ChatMessage = ({ message, type, onEdit, chatbotName, selectedSessionMetadata }) => {
  const { t } = useLanguage();
  const { getModelDisplayName, getModelConfig } = useAIModels();
  const isUser = type === 'question';
  const content = isUser ? message.question : message.answer;
  const timestamp = new Date(message.timestamp).toLocaleString();

  const hasLikeStatus = !isUser && 'liked' in message && message.liked !== "none";

  // Get model information from the message using context
  const modelInfo = !isUser && message.model ? getModelInfo(message.model, t, getModelDisplayName, getModelConfig) : null;

  return (
    <div className={`chat-message ${isUser ? 'bot-message' : 'user-message'}`}>
      <div className="message-avatar">
        {!isUser ? <img src="/logo192.png" alt={t('dashboard.agentchats.userLabel')} className="avatar-icon user-icon" /> :
          <FiUser className="avatar-icon bot-icon" />}
      </div>
      <div className="message-content">
        <div className="message-header">
          <div className="d-flex align-items-center" style={{ paddingRight: '5px' }}>
            <span className="message-sender">
              {!isUser
                ? (message.model === 'human'
                  ? (message.agent_display_name || t('dashboard.agentchats.filterHuman'))
                  : (chatbotName || t('dashboard.agentchats.agentLabel'))
                )
                : (selectedSessionMetadata?.lead_info?.fullName || selectedSessionMetadata?.lead_info?.name || t('dashboard.agentchats.userLabel'))
              }
            </span>
            {hasLikeStatus && (
              <Badge
                bg={message.liked === "liked" ? 'success' : 'danger'}
                className="d-flex align-items-center"
              >
                {message.liked === "liked" ? (
                  <><FiThumbsUp size={12} /></>
                ) : (
                  <><FiThumbsDown size={12} /></>
                )}
              </Badge>
            )}
            {/* Display model info if this is an answer and has model data */}
            {!isUser && modelInfo && modelInfo.name !== t('dashboard.agentchats.filterHuman') && modelInfo.name !== 'human' && (
              <div className="model-info d-flex align-items-center" style={{ marginRight: '1rem' }}>
                <Badge bg="light" text="dark" className="d-flex align-items-center">
                  <span style={{ color: modelInfo.color }} className="me-1">{modelInfo.icon}</span>
                  <span className="model-name">{modelInfo.name}</span>
                </Badge>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center">
            <small className="message-time">{timestamp}</small>
            {!isUser && message.model !== 'human' && (
              <LinkButton
                onClick={() => onEdit(message)}
                title={t('dashboard.agentchats.editButtonLabel')}
              >
                <FiEdit size={16} />
              </LinkButton>
            )}
          </div>
        </div>
        <div className="message-body">
          <style>{`
            .message-body {
              font-size: 0.875rem;
              max-width: 100%;
              line-height: 1.7;
            }
            .message-body p {
              margin-bottom: 0.5rem;
              line-height: 1.625;
            }
            .message-body p:last-child {
              margin-bottom: 0;
            }
            .message-body br {
              display: block;
              margin-bottom: 0.375rem;
            }
            .message-body h1 {
              font-size: 1.125rem;
              font-weight: 700;
              margin-bottom: 0.5rem;
              margin-top: 0.5rem;
            }
            .message-body h1:first-child {
              margin-top: 0;
            }
            .message-body h2 {
              font-size: 1rem;
              font-weight: 700;
              margin-bottom: 0.5rem;
              margin-top: 0.5rem;
            }
            .message-body h2:first-child {
              margin-top: 0;
            }
            .message-body h3 {
              font-size: 0.875rem;
              font-weight: 700;
              margin-bottom: 0.375rem;
              margin-top: 0.5rem;
            }
            .message-body h3:first-child {
              margin-top: 0;
            }
            .message-body ul {
              list-style-type: disc;
              list-style-position: inside;
              margin-bottom: 0.75rem;
              padding-left: 0;
            }
            .message-body ol {
              list-style-type: decimal;
              list-style-position: inside;
              margin-bottom: 1rem;
              padding-left: 0;
            }
            .message-body li {
              margin-bottom: 0.25rem;
            }
            .message-body blockquote {
              border-left: 4px solid rgba(107, 114, 128, 0.3);
              padding-left: 1rem;
              font-style: italic;
              margin: 1rem 0;
            }
            .message-body code {
              background-color: rgba(0, 0, 0, 0.05);
              padding: 0.125rem 0.375rem;
              border-radius: 0.25rem;
              font-size: 0.875rem;
              font-family: 'Courier New', Courier, monospace;
              display: inline;
              margin: 0;
              line-height: inherit;
              vertical-align: baseline;
            }
            .message-body pre {
              margin: 1rem 0;
            }
            .message-body pre code {
              display: block;
              background-color: rgba(0, 0, 0, 0.05);
              padding: 1rem;
              border-radius: 0.5rem;
              overflow-x: auto;
              font-size: 0.875rem;
              font-family: 'Courier New', Courier, monospace;
            }
            .message-body a {
              color: #532A7E;
              text-decoration: none;
            }
            .message-body a:hover {
              text-decoration: underline;
            }
            .message-body strong {
              font-weight: 700;
            }
            .message-body em {
              font-style: italic;
            }
            .message-body table {
              width: 100%;
              border-collapse: collapse;
              margin: 1rem 0;
              overflow-x: auto;
              display: block;
            }
            .message-body thead {
              background-color: rgba(0, 0, 0, 0.05);
            }
            .message-body tbody {
              border-top: 1px solid rgba(0, 0, 0, 0.1);
            }
            .message-body tr {
              border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            }
            .message-body th {
              padding: 0.5rem 1rem;
              text-align: left;
              font-size: 0.875rem;
              font-weight: 600;
            }
            .message-body td {
              padding: 0.5rem 1rem;
              font-size: 0.875rem;
            }
            .message-body img {
              max-width: 100%;
              max-height: 400px;
              width: auto;
              height: auto;
              object-fit: contain;
              border-radius: 0.5rem;
              margin: 1rem 0;
              display: block;
            }
            .message-body hr {
              border: none;
              border-top: 1px solid rgba(0, 0, 0, 0.1);
              margin: 1rem 0;
            }
          `}</style>
          <div
            dangerouslySetInnerHTML={{
              __html: marked(content),
            }}
          />
        </div>
      </div>
    </div>
  );
};

// Helper function to get provider icon by name
const getProviderIcon = (provider) => {
  switch (provider?.toLowerCase()) {
    case 'google':
      return <SiGoogle size={14} />;
    case 'openai':
      return <SiOpenai size={14} />;
    case 'anthropic':
      return <span className="icon-anthropic" style={{ fontSize: '15px' }}>A</span>;
    case 'xai':
      return <span className="icon-xai" style={{ fontSize: '15px' }}>X</span>;
    default:
      return <FiCpu size={14} />;
  }
};

// Helper function to get provider color
const getProviderColor = (provider) => {
  const colorMap = {
    'Google': '#4285F4',
    'OpenAI': '#10a37f',
    'Anthropic': '#b300ff',
    'xAI': '#1d9bf0'
  };
  return colorMap[provider] || '#6B7280';
};

// Add a helper function to get model information (uses context functions)
const getModelInfo = (modelId, t, getModelDisplayName, getModelConfig) => {
  // Special case for human model
  if (modelId === 'human') {
    return {
      name: t('dashboard.agentchats.filterHuman'),
      icon: <FiUser size={14} />,
      color: '#28a745'
    };
  }

  // Use context to get model info
  const modelConfig = getModelConfig(modelId);
  if (modelConfig) {
    return {
      name: modelConfig.displayName || getModelDisplayName(modelId),
      icon: getProviderIcon(modelConfig.provider),
      color: getProviderColor(modelConfig.provider)
    };
  }

  // Fallback for unknown models
  return {
    name: getModelDisplayName(modelId),
    icon: <FiCpu size={14} />,
    color: '#6B7280'
  };
};

// Chat sidebar item component
const ChatSidebarItem = ({ sessionMetadata, isActive, onClick, onDelete }) => {
  const { t } = useLanguage();
  const timestamp = sessionMetadata.last_updated
    ? new Date(sessionMetadata.last_updated).toLocaleString()
    : t('dashboard.agentchats.notAvailable');

  // Display last message or fallback to session ID
  const lastMessage = sessionMetadata.last_message || `Session ...${sessionMetadata.session_id?.substring(sessionMetadata.session_id.length - 8)}`;
  // Truncate if too long (max 45 characters)
  const displayMessage = lastMessage.length > 30 ? `${lastMessage.substring(0, 30)}...` : lastMessage;

  return (
    <div
      className={`chat-sidebar-item ${isActive ? 'active' : ''}`}
    >
      <div className="sidebar-item-content" onClick={onClick}>
        <div className="sidebar-item-avatar">
          <FiUser className="sidebar-avatar-icon" />
        </div>
        <div className="sidebar-item-details">
          <div className="sidebar-item-message">{displayMessage}</div>
          <div className="sidebar-item-metadata">
            <FiClock className="sidebar-metadata-icon" />
            <span>{timestamp}</span>
          </div>
        </div>
      </div>
      <button
        className="delete-session-btn"
        onClick={(e) => onDelete(sessionMetadata.session_id, e)}
        title={t('dashboard.agentchats.deleteSessionButton')}
        aria-label={t('dashboard.agentchats.deleteSessionButton')}
      >
        <FiTrash2 size={16} />
      </button>
    </div>
  );
};

// Main AgentChats component
const AgentChats = () => {
  const { id: agentId } = useParams();
  const { t } = useLanguage();
  const { apiUrl } = useGlobal();
  const { workspaceData, currentWorkspace, switchWorkspace } = useWorkspace();
  const chatContainerRef = useRef(null);
  const chatMainContentRef = useRef(null);
  const fetchingRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State for chats data
  // Session-based state
  const [sessionsData, setSessionsData] = useState({ sessions: [], total_pages: 0, current_page: 1, total_count: 0 });
  const [currentSessionChats, setCurrentSessionChats] = useState([]);
  const [selectedSessionMetadata, setSelectedSessionMetadata] = useState(null);
  const [, setLoadingChats] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add states for chatbot name and human support agent name
  const [chatbotName, setChatbotName] = useState("");
  const [supportAgentName, setSupportAgentName] = useState("");

  // Selected chat state
  const [selectedSession, setSelectedSession] = useState(null);

  // QA Modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [qaData, setQaData] = useState({ title: '', question: '', answer: '' });

  // Delete session popup state
  const [deletePopup, setDeletePopup] = useState({ isOpen: false, sessionId: null });

  // Tour state
  const [showTour, setShowTour] = useState(false);

  // Human support WebSocket state
  const [humanMessage, setHumanMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [editorKey, setEditorKey] = useState(0); // Key to force MDXEditor remount
  const [connectionState, setConnectionState] = useState('disconnected'); // 'connected', 'connecting', 'disconnected'
  const [messageQueue, setMessageQueue] = useState([]); // Queue of messages to send/retry
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const pollingIntervalRef = useRef(null); // For session list polling
  const autoOpenProcessedRef = useRef(false); // Track if auto-open from URL has been processed
  const lastFetchTimestampRef = useRef({}); // Track last fetch time per session to prevent excessive fetching

  // Refs to track current selected session without causing callback recreation
  const selectedSessionRef = useRef(selectedSession);
  const selectedSessionMetadataRef = useRef(selectedSessionMetadata);

  // Keep refs in sync with state
  useEffect(() => {
    selectedSessionRef.current = selectedSession;
  }, [selectedSession]);

  useEffect(() => {
    selectedSessionMetadataRef.current = selectedSessionMetadata;
  }, [selectedSessionMetadata]);

  // Calculate min date based on workspace chat history limit
  const getMinDate = () => {
    if (!workspaceData || !workspaceData.workspace_bv_chat_history_day_limit) return null;

    const minDate = new Date();
    minDate.setDate(minDate.getDate() - workspaceData.workspace_bv_chat_history_day_limit);
    return minDate;
  };


  // Filter options from API
  const [filterOptions, setFilterOptions] = useState({
    countries: [],
    cities: [],
    message_sources: [],
    models: []
  });


  // Separate current filters (what user is selecting) from applied filters (what's actually used for search)
  const [currentFilters, setCurrentFilters] = useState({
    country: 'all',
    city: 'all',
    source: 'all',
    start_date: null,
    end_date: null,
    page: 1,
    limit: 8,
    sortOrder: 'newest',
    likeStatus: 'all',
    model: 'all',
    type: 'all'
  });

  // Applied filters state - what's actually used in API calls
  const [filters, setFilters] = useState({
    country: 'all',
    city: 'all',
    source: 'all',
    start_date: null,
    end_date: null,
    page: 1,
    limit: 8,
    sortOrder: 'newest',
    likeStatus: 'all',
    model: 'all',
    type: 'all'
  });

  // Filter visibility for mobile
  const [showFilters, setShowFilters] = useState(false);

  // Details sidebar visibility for mobile
  const [showDetails, setShowDetails] = useState(false);

  // Available filter options that are static
  const typeOptions = [
    { value: 'all', label: t('dashboard.agentchats.filterAll') },
    { value: 'AiAgent', label: t('dashboard.agentchats.filterAI') },
    { value: 'HumanSupport', label: t('dashboard.agentchats.filterHuman') }
  ];

  const likeStatusOptions = [
    { value: 'all', label: t('dashboard.agentchats.filterAllFeedback') },
    { value: 'liked', label: t('dashboard.agentchats.filterLiked') },
    { value: 'disliked', label: t('dashboard.agentchats.filterDisliked') },
    { value: 'none', label: t('dashboard.agentchats.filterNoFeedback') }
  ];

  const sortOptions = [
    { value: 'newest', label: t('dashboard.agentchats.filterNewest') },
    { value: 'oldest', label: t('dashboard.agentchats.filterOldest') }
  ];

  // Add state to track which dropdown is open
  const [openDropdown, setOpenDropdown] = useState(null);
  const [, setDropdownPosition] = useState({});

  // Function to handle dropdown toggle with positioning (used internally)
  // eslint-disable-next-line no-unused-vars
  const handleDropdownToggle = (isOpen, dropdownName, event) => {
    if (isOpen) {
      setOpenDropdown(dropdownName);

      // Calculate dropdown position
      if (event?.target) {
        const rect = event.target.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownMaxHeight = 320;

        // Determine if dropdown should open upward or downward
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        let top, maxHeight;
        if (spaceBelow >= dropdownMaxHeight || spaceBelow >= spaceAbove) {
          // Open downward
          top = rect.bottom + window.scrollY + 4;
          maxHeight = Math.min(dropdownMaxHeight, spaceBelow - 20);
        } else {
          // Open upward
          top = rect.top + window.scrollY - Math.min(dropdownMaxHeight, spaceAbove - 20);
          maxHeight = Math.min(dropdownMaxHeight, spaceAbove - 20);
        }

        setDropdownPosition({
          top,
          left: rect.left + window.scrollX,
          width: rect.width,
          maxHeight
        });
      }
    } else if (openDropdown === dropdownName) {
      setOpenDropdown(null);
      setDropdownPosition({});
    }
  };

  // Fetch filter options from API
  const fetchFilterOptions = useCallback(async () => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      const url = `${apiUrl}/api/v1/chatbot/get-chatbot-chats-filters/${agentId}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${brandvoxToken}` }
      });

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.errorFetchData'));
      }

      const data = await response.json();

      // Add t('dashboard.agentchats.filterAll') option to each filter list
      const allOption = { value: 'all', label: t('dashboard.agentchats.filterAll') };

      setFilterOptions({
        countries: [allOption, ...(data.filters.countries || []).map(country => ({ value: country.value, label: country.label }))],
        cities: [allOption, ...(data.filters.cities || []).map(city => ({ value: city.value, label: city.label }))],
        message_sources: [allOption, ...(data.filters.message_sources || []).map(source => ({ value: source.value, label: source.label }))],
        models: [allOption, ...(data.filters.models || []).map(model => ({ value: model.value, label: model.label }))]
      });
    } catch (err) {
      // Don't set error state, just use default filters if this fails
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, agentId]);

  // Update handleFilterChange to only update currentFilters without triggering search
  const handleFilterChange = (filterName, value) => {
    setCurrentFilters(prev => {
      const newFilters = {
        ...prev,
        [filterName]: value
      };

      return newFilters;
    });
  };

  // Special handler for pagination that still triggers immediate search
  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
    setFiltersApplied(true);

    // Scroll to top immediately on mobile with a slight delay
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  };

  // Handle date changes for DateTimePicker
  const handleDateChange = (type, value) => {
    setCurrentFilters(prev => {
      const newFilters = {
        ...prev,
        [type]: value
      };

      return newFilters;
    });
  };


  // Apply filters function - copies currentFilters to filters and triggers search
  const applyFilters = () => {
    const newFilters = {
      ...currentFilters,
      page: 1
    };

    // Apply all current filters and reset to page 1
    setFilters(newFilters);
    setFiltersApplied(true);

    // Hide filters sidebar after search only on mobile
    if (window.innerWidth <= 768) {
      setShowFilters(false);
    }

    // Don't call fetchChats here - let the useEffect handle it when filters change
  };

  // Reset filters function
  const resetFilters = () => {
    const defaultFilters = {
      country: 'all',
      city: 'all',
      source: 'all',
      start_date: null,
      end_date: null,
      page: 1,
      limit: 8,
      sortOrder: 'newest',
      likeStatus: 'all',
      model: 'all',
      type: 'all'
    };

    // Reset both current and applied filters
    setCurrentFilters(defaultFilters);
    setFilters(defaultFilters);
    setFiltersApplied(true); // Mark that filters have been applied

    // Don't call fetchChats here - let the useEffect handle it when filters change
  };

  // Reset filters with forced fetch for empty state
  const resetFiltersWithFetch = () => {
    const defaultFilters = {
      country: 'all',
      city: 'all',
      source: 'all',
      start_date: null,
      end_date: null,
      page: 1,
      limit: 8,
      sortOrder: 'newest',
      likeStatus: 'all',
      model: 'all',
      type: 'all'
    };

    // Reset both current and applied filters
    setCurrentFilters(defaultFilters);
    setFilters(defaultFilters);
    setFiltersApplied(true); // Mark that filters have been applied

    // Don't call fetchChats here - let the useEffect handle it when filters change
  };

  // Fetch chats data
  // Fetch sessions (without chats)
  const fetchSessions = useCallback(async (isPolling = false) => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    if (!isPolling) {
      setError(null);
    }

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      const { country, city, source, start_date, end_date, page, limit, likeStatus, model, type } = filters;

      // Format dates if they exist with proper ISO format
      const formattedStartDate = start_date ? new Date(start_date).toISOString() : null;
      const formattedEndDate = end_date ? new Date(end_date).toISOString() : null;

      // Build request body for the new v1 API
      const requestBody = {
        chatbot_id: agentId,
        page: page,
        limit: limit,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        filters: {
          country: country !== 'all' ? country : undefined,
          city: city !== 'all' ? city : undefined,
          message_source: source !== 'all' ? source : undefined,
          model: model !== 'all' ? model : undefined,
          liked: likeStatus !== 'all' ? likeStatus : undefined,
          type: type !== 'all' ? type : undefined
        }
      };

      // Remove undefined values from filters
      Object.keys(requestBody.filters).forEach(key => {
        if (requestBody.filters[key] === undefined) {
          delete requestBody.filters[key];
        }
      });

      const url = `${apiUrl}/api/v1/chatbot/get-chatbot-sessions`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.errorFetchData'));
      }

      const data = await response.json();

      // If polling, merge intelligently
      if (isPolling) {
        setSessionsData(prev => {
          const newSessions = data.sessions || [];
          const existingSessions = prev.sessions || [];

          // Create a map of existing sessions by ID for quick lookup
          const existingSessionMap = {};
          existingSessions.forEach(session => {
            existingSessionMap[session.session_id] = session;
          });

          // Merge: update existing sessions, prepend new ones
          const mergedSessions = newSessions.map(newSession => {
            const existingSession = existingSessionMap[newSession.session_id];
            if (existingSession) {
              // Update existing session metadata
              return newSession;
            }
            return newSession;
          });

          return {
            sessions: mergedSessions,
            total_pages: data.total_pages || 0,
            current_page: data.current_page || 1,
            total_count: data.total_count || 0
          };
        });

        // Update selected session metadata if it changed
        if (selectedSessionMetadataRef.current) {
          const updatedSelected = (data.sessions || []).find(
            s => s.session_id === selectedSessionMetadataRef.current.session_id
          );
          if (updatedSelected) {
            setSelectedSessionMetadata(updatedSelected);
          }
        }
      } else {
        // Not polling - full replace
        setSessionsData({
          sessions: data.sessions || [],
          total_pages: data.total_pages || 0,
          current_page: data.current_page || 1,
          total_count: data.total_count || 0
        });

        // Select first session and load its chats (only on initial load)
        // BUT skip if there are URL params for auto-opening a specific session
        const sessionParam = searchParams.get('session');
        const autoParam = searchParams.get('auto');
        const hasAutoOpenParams = sessionParam && autoParam === 'true';

        if (data.sessions && data.sessions.length > 0 && !selectedSessionRef.current && !hasAutoOpenParams) {
          // Sort sessions before selecting first one to match display order
          const sortedSessions = [...data.sessions].sort((a, b) => {
            const timeA = new Date(a.last_updated || a.created_time).getTime();
            const timeB = new Date(b.last_updated || b.created_time).getTime();

            // Default to newest first (descending order)
            if (filters.sortOrder === 'oldest') {
              return timeA - timeB; // Ascending order (oldest first)
            }
            return timeB - timeA; // Descending order (newest first)
          });

          const firstSession = sortedSessions[0];
          setSelectedSession(firstSession.session_id);
          setSelectedSessionMetadata(firstSession);
          // Load chats for first session
          await fetchSessionChats(firstSession.session_id);
        }
      }
    } catch (err) {
      if (!isPolling) {
        setError(err.message || t('dashboard.agentchats.errorUnknown'));
      }
    } finally {
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, agentId, filters]);

  // Fetch chats for a specific session (lazy loading)
  const fetchSessionChats = useCallback(async (sessionId) => {
    // Debounce: Prevent excessive fetching - only fetch if 2 seconds have passed since last fetch
    const now = Date.now();
    const lastFetch = lastFetchTimestampRef.current[sessionId] || 0;
    const timeSinceLastFetch = now - lastFetch;
    const DEBOUNCE_DELAY = 5000; // 5 seconds

    if (timeSinceLastFetch < DEBOUNCE_DELAY) {

      return;
    }

    // Update last fetch timestamp
    lastFetchTimestampRef.current[sessionId] = now;

    setLoadingChats(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      const requestBody = {
        chatbot_id: agentId,
        session_id: sessionId
      };

      const url = `${apiUrl}/api/v1/chatbot/get-session-chats`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.errorFetchData'));
      }

      const data = await response.json();

      // Sort messages by timestamp (ascending for chat display)
      const sortedChats = (data.chats || []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Intelligent merge: Backend is source of truth, but preserve optimistic UI messages
      setCurrentSessionChats(prevChats => {
        // Keep only pending messages (optimistic UI - not yet confirmed by backend)
        const pendingMessages = prevChats.filter(msg => msg.isPending);

        // Create a set of backend message keys for deduplication
        const backendMessageKeys = new Set(
          sortedChats
            .filter(msg => msg.message_key)
            .map(msg => msg.message_key)
        );

        // Filter out pending messages that have been confirmed by backend
        const uniquePendingMessages = pendingMessages.filter(msg => {
          // If pending message has a message_key and it's in backend data, remove it (confirmed)
          if (msg.message_key && backendMessageKeys.has(msg.message_key)) {
            return false;
          }
          // Keep pending messages that haven't been confirmed yet
          return true;
        });

        // Merge: backend messages + remaining pending messages
        const mergedChats = [...sortedChats, ...uniquePendingMessages].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );

        // Only update if there are actual changes
        if (JSON.stringify(prevChats) === JSON.stringify(mergedChats)) {
          return prevChats;
        }

        return mergedChats;
      });

      // Update selectedSessionMetadata if session_metadata is in the response
      if (data.session_metadata) {
        setSelectedSessionMetadata(prev => {
          // Preserve lead_info if it exists in prev but not in new data
          const preservedLeadInfo = (!data.session_metadata.lead_info && prev?.lead_info)
            ? { lead_info: prev.lead_info }
            : {};

          return {
            ...prev,
            ...data.session_metadata,
            ...preservedLeadInfo
          };
        });
      }
    } catch (err) {
      setError(err.message || t('dashboard.agentchats.errorUnknown'));
      setCurrentSessionChats([]);
    } finally {
      setLoadingChats(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, agentId]);

  // Fetch chatbot name and agent display name
  const fetchAgentName = useCallback(async () => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      // Fetch chatbot name
      const cachedAgentNames = JSON.parse(localStorage.getItem('cached_agent_names') || '{}');
      if (cachedAgentNames[agentId]) {
        setChatbotName(cachedAgentNames[agentId]);
      } else {
        const chatbotResponse = await fetch(
          `${apiUrl}/api/v1/chatbot/get-chatbot`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ chatbot_id: agentId })
          }
        );

        if (chatbotResponse.ok) {
          const data = await chatbotResponse.json();
          if (data && data.chatbot_name) {
            setChatbotName(data.chatbot_name);
          }
        }
      }

      // Fetch agent display name for human support
      if (workspaceData?.workspace_id) {
        const agentNameResponse = await fetch(
          `${apiUrl}/api/v1/workspace/member/agent-display-name?workspace_id=${workspaceData.workspace_id}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (agentNameResponse.ok) {
          const data = await agentNameResponse.json();
          if (data && data.agent_display_name) {
            setSupportAgentName(data.agent_display_name);
          }
        }
      }
    } catch (err) {
      // Error handled silently
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, agentId, workspaceData]);

  // Save agent display name to workspace member
  const saveAgentName = useCallback(async (name) => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken || !workspaceData?.workspace_id) {
        return false;
      }

      const response = await fetch(
        `${apiUrl}/api/v1/workspace/member/agent-display-name`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${brandvoxToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            workspace_id: workspaceData.workspace_id,
            agent_display_name: name
          })
        }
      );

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.errorSaveAgentName'));
      }

      return true;
    } catch (err) {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, workspaceData]);

  // Track the currently connected session to prevent duplicate connections
  const connectedSessionRef = useRef(null);

  // WebSocket connection management for human support
  const connectWebSocket = useCallback((sessionId) => {

    // Prevent connecting to the same session if already connected
    if (connectedSessionRef.current === sessionId && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    // Clear any pending reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close(1000, 'Switching sessions'); // Use code 1000 to prevent auto-reconnect
      wsRef.current = null;
      connectedSessionRef.current = null;
    }

    // Clear existing ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    setConnectionState('connecting');

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      if (!brandvoxToken || !sessionId || !agentId) {
        setConnectionState('disconnected');
        return;
      }

      // Build WebSocket URL (replace http/https with ws/wss)
      const wsUrl = apiUrl.replace(/^http/, 'ws');
      const ws = new WebSocket(
        `${wsUrl}/api/v1/ws/human-support/${agentId}?token=${brandvoxToken}&session_id=${sessionId}`
      );

      ws.onopen = () => {
        setConnectionState('connected');
        reconnectAttempts.current = 0; // Reset reconnection attempts on successful connection
        connectedSessionRef.current = sessionId; // Track connected session

        // Refresh chat history to get latest messages when connection is established
        if (selectedSessionRef.current === sessionId) {
          fetchSessionChats(sessionId);
        }

        // Start ping interval (every 25 seconds)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: 'ping',
              timestamp: new Date().toISOString()
            }));
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'new_user_message') {
            // User sent a new message - reload chat messages
            if (selectedSessionRef.current === sessionId) {
              fetchSessionChats(sessionId);
            }
          } else if (data.type === 'message_sent') {
            // Human message was sent successfully
            setSendingMessage(false);

            // Update message queue status
            if (data.client_message_id) {
              setMessageQueue(prev => prev.map(msg =>
                msg.id === data.client_message_id
                  ? { ...msg, status: 'sent' }
                  : msg
              ));

              // Update optimistic message to confirmed
              setCurrentSessionChats(prev => prev.map(msg =>
                msg.clientMessageId === data.client_message_id
                  ? { ...msg, isPending: false, message_key: data.message_key }
                  : msg
              ));
            } else {
              // Fallback: reload chat messages if no client_message_id (old backend)
              if (selectedSessionRef.current === sessionId) {
                fetchSessionChats(sessionId);
              }
            }
          } else if (data.type === 'pong') {
            // Pong received from server
          } else if (data.type === 'session_type_changed') {
            // Session type changed (AI -> Human or Human -> AI)
            const newType = data.data?.new_type;

            // Update selected session metadata if this is the current session
            if (selectedSessionRef.current === sessionId && newType) {
              setSelectedSessionMetadata(prev => {
                if (prev) {
                  return { ...prev, type: newType };
                }
                return prev;
              });

              // Auto-switch to the correct tab in left sidebar
              // The useEffect watching filters will automatically trigger fetchSessions
              setFilters(prev => ({
                ...prev,
                type: newType,
                page: 1 // Reset to first page
              }));
            }
          } else if (data.type === 'error') {
            // Error from server
            setSendingMessage(false);
          }
        } catch (err) {
        }
      };

      ws.onerror = (error) => {
        setConnectionState('disconnected');
        setSendingMessage(false);
      };

      ws.onclose = (event) => {
        setConnectionState('disconnected');
        setSendingMessage(false);

        // Clear connected session tracking
        if (connectedSessionRef.current === sessionId) {
          connectedSessionRef.current = null;
        }

        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Auto-reconnect after delay (unless manual disconnect - code 1000)
        // Only reconnect if:
        // 1. Not a manual disconnect (code 1000)
        // 2. We still have a selected session
        // 3. The closed session matches the currently selected session
        if (event.code !== 1000 && selectedSessionRef.current === sessionId) {
          reconnectAttempts.current += 1;
          // Improved reconnection: 500ms initial delay, max 10s (instead of 1s initial, 30s max)
          const delay = Math.min(500 * Math.pow(2, reconnectAttempts.current - 1), 10000);

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket(selectedSessionRef.current);
          }, delay);
        } else {
        }
      };

      wsRef.current = ws;
    } catch (err) {
      setConnectionState('disconnected');
    }
  }, [apiUrl, agentId, fetchSessionChats]);

  // Process message queue and send messages
  const sendQueuedMessage = useCallback(async (queuedMessage) => {
    // Ensure connection is open
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      // Mark as failed if can't send
      setMessageQueue(prev => prev.map(msg =>
        msg.id === queuedMessage.id
          ? { ...msg, status: 'failed' }
          : msg
      ));
      return;
    }

    try {
      // Update status to sending
      setMessageQueue(prev => prev.map(msg =>
        msg.id === queuedMessage.id
          ? { ...msg, status: 'sending' }
          : msg
      ));

      wsRef.current.send(JSON.stringify({
        type: 'send_message',
        message: queuedMessage.content,
        client_message_id: queuedMessage.id, // Include client ID for tracking
        agent_display_name: supportAgentName // Include agent display name
      }));

      // Backend will send confirmation, which will update the status
    } catch (err) {
      setMessageQueue(prev => prev.map(msg =>
        msg.id === queuedMessage.id
          ? { ...msg, status: 'failed' }
          : msg
      ));
    }
  }, [supportAgentName]);

  // Send message via WebSocket with queue
  const sendHumanMessage = useCallback(() => {
    if (!humanMessage.trim()) {
      return;
    }

    // Validate agent name is set
    if (!supportAgentName || !supportAgentName.trim()) {
      alert('Please enter your name before sending a message');
      return;
    }

    // Create queued message
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const queuedMessage = {
      id: messageId,
      content: humanMessage,
      timestamp: new Date().toISOString(),
      status: 'pending' // 'pending', 'sending', 'sent', 'failed'
    };

    // Add to queue
    setMessageQueue(prev => [...prev, queuedMessage]);

    // Add optimistic UI update - show message immediately in chat
    const optimisticMessage = {
      question: '', // Empty for human messages
      answer: humanMessage,
      timestamp: new Date().toISOString(),
      model: 'human',
      agent_display_name: supportAgentName,
      isPending: true,
      clientMessageId: messageId
    };
    setCurrentSessionChats(prev => [...prev, optimisticMessage]);

    // Clear input immediately (optimistic UI)
    setHumanMessage('');
    setEditorKey(prev => prev + 1);

    // Auto-connect if not connected
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      if (selectedSession && connectionState === 'disconnected') {
        connectWebSocket(selectedSession);
      }
      return;
    }

    // Send immediately if connected
    setSendingMessage(true);
    sendQueuedMessage(queuedMessage);
  }, [humanMessage, supportAgentName, selectedSession, connectionState, connectWebSocket, sendQueuedMessage]);

  // WebSocket connection effect - connect when session changes
  useEffect(() => {

    if (selectedSession && agentId) {
      connectWebSocket(selectedSession);
    }

    // Cleanup function
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component cleanup'); // Use code 1000 to prevent auto-reconnect
        wsRef.current = null;
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      connectedSessionRef.current = null;
    };
  }, [selectedSession, agentId, connectWebSocket]);

  // Process pending messages when connection is established
  useEffect(() => {
    if (connectionState === 'connected' && wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const pendingMessages = messageQueue.filter(msg => msg.status === 'pending' || msg.status === 'failed');
      if (pendingMessages.length > 0) {
        pendingMessages.forEach(msg => {
          sendQueuedMessage(msg);
        });
      }
    }
  }, [connectionState, messageQueue, sendQueuedMessage]);

  // Add agent name fetch to initial loading
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch filter options and agent name in parallel
        await Promise.all([
          fetchFilterOptions(),
          fetchAgentName()
        ]);
        // Call fetchSessions after filter options are loaded
        await fetchSessions();
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]); // Only depend on agentId

  // Track if filters have been applied at least once
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Separate useEffect to handle filter changes (including pagination)
  useEffect(() => {
    // Skip initial load (when all filters are at default and it's the first render)
    if (filtersApplied ||
      filters.country !== 'all' || filters.city !== 'all' ||
      filters.source !== 'all' || filters.model !== 'all' || filters.likeStatus !== 'all' ||
      filters.type !== 'all' ||
      filters.start_date || filters.end_date || filters.sortOrder !== 'newest') {
      fetchSessions();
      // Reset the flag after triggering the fetch
      if (filtersApplied) {
        setFiltersApplied(false);
      }
    }
  }, [filters, fetchSessions, filtersApplied]);

  // Polling interval for session list updates (every 10 seconds)
  useEffect(() => {
    // Start polling interval
    pollingIntervalRef.current = setInterval(() => {
      fetchSessions(true); // Pass true to indicate polling mode
    }, 10000); // 10 seconds

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [fetchSessions]);

  // Polling interval for chat messages of selected session (every 5 seconds)
  // This ensures messages appear even when WebSocket is disconnected
  useEffect(() => {
    if (selectedSession) {
      const chatPollingInterval = setInterval(() => {
        fetchSessionChats(selectedSession);
      }, 5000); // 5 seconds

      return () => {
        clearInterval(chatPollingInterval);
      };
    }
  }, [selectedSession, fetchSessionChats]);

  // Handle auto-opening of session from URL query parameters (from email deep link)
  // Enhanced version with workspace validation and direct session fetch
  useEffect(() => {
    const sessionParam = searchParams.get('session');
    const autoParam = searchParams.get('auto');

    // Only process if we have session and auto parameters and haven't processed yet
    if (sessionParam && autoParam === 'true' && !autoOpenProcessedRef.current && currentWorkspace) {
      // Mark as processed immediately to prevent duplicate calls
      autoOpenProcessedRef.current = true;

      // Make async to handle workspace switching and session fetching
      (async () => {
        try {
          // Step 1: Fetch session info (includes workspace_id)
          const brandvoxToken = localStorage.getItem('brandvox_token');

          if (!brandvoxToken) {
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }

          const sessionInfoResponse = await fetch(
            `${apiUrl}/api/v1/chatbot/get-session-info?session_id=${sessionParam}&chatbot_id=${agentId}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${brandvoxToken}`
              }
            }
          );

          if (!sessionInfoResponse.ok) {
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }

          const sessionInfo = await sessionInfoResponse.json();

          // Check if session was found
          if (!sessionInfo.session_found) {
            console.warn(`Session not found or deleted: ${sessionParam}`);
            window.history.replaceState({}, '', window.location.pathname);
            return;
          }

          // Step 2: Validate workspace and switch if needed
          if (sessionInfo.workspace_id !== currentWorkspace.workspace_id) {
            await switchWorkspace(sessionInfo.workspace_id);
            // Note: The component will re-render after workspace switch
            // The URL parameters will still be there, but autoOpenProcessedRef will be reset
            // We need to rely on the sessions being loaded after workspace switch
            autoOpenProcessedRef.current = false; // Reset for next attempt
            return;
          }

          // Step 3: Direct session fetch - try to find in already loaded sessions first
          let sessionToOpen = sessionsData.sessions.find(
            (session) => session.session_id === sessionParam
          );

          // If not found in loaded sessions, fetch the specific session
          if (!sessionToOpen && sessionsData.sessions.length > 0) {
            // Session exists but not on current page - fetch session chats directly
            const sessionChatsResponse = await fetch(
              `${apiUrl}/api/v1/chatbot/get-session-chats`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${brandvoxToken}`
                },
                body: JSON.stringify({
                  chatbot_id: agentId,
                  session_id: sessionParam
                })
              }
            );

            if (sessionChatsResponse.ok) {
              const sessionData = await sessionChatsResponse.json();

              // Create a session metadata object from the response
              if (sessionData.session_metadata) {
                sessionToOpen = sessionData.session_metadata;
                // Open the session directly
                await handleChatSelect(sessionToOpen);
              }
            }
          } else if (sessionToOpen) {
            // Found in loaded sessions, open it
            await handleChatSelect(sessionToOpen);
          }

          // Clean URL without remounting
          window.history.replaceState({}, '', window.location.pathname);

        } catch (error) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentWorkspace, sessionsData.sessions.length]);

  // Check if tour should be shown on first visit
  useEffect(() => {
    const tourCompleted = localStorage.getItem(`human_support_tour_completed_${agentId}`);

    // Show tour if not completed before and sessions are loaded but no data exists
    if (!tourCompleted && !loading && sessionsData.sessions.length === 0 && !error) {
      const timer = setTimeout(() => {
        setShowTour(true);
      }, 500); // Small delay for better UX
      return () => clearTimeout(timer);
    }
  }, [agentId, loading, sessionsData.sessions.length, error]);

  // Position chat at bottom when opening - using useLayoutEffect for synchronous positioning
  useLayoutEffect(() => {
    if (selectedSession && chatContainerRef.current) {
      // This runs synchronously after DOM mutations but before paint
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [selectedSession, currentSessionChats]);

  // Handle filter toggle with scroll position management
  const handleFilterToggle = () => {
    // Toggle filter visibility
    setShowFilters(!showFilters);

    // On mobile, scroll to top of filters when opening
    if (!showFilters && window.innerWidth <= 768) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  // Handle details toggle for mobile
  const handleDetailsToggle = () => {
    setShowDetails(!showDetails);
  };

  // Handle chat selection
  const handleChatSelect = async (sessionMetadata) => {
    // Don't re-select if already selected (prevents unnecessary re-renders)
    if (selectedSession === sessionMetadata.session_id) {
      return;
    }

    setSelectedSession(sessionMetadata.session_id);
    setSelectedSessionMetadata(sessionMetadata);

    // Fetch chats for this session
    await fetchSessionChats(sessionMetadata.session_id);

    // On mobile, scroll to show the chat area.
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        if (chatMainContentRef.current) {
          chatMainContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  // Sort chat sessions based on sort order
  const getSortedSessions = () => {
    if (!sessionsData.sessions) return [];

    // Sort sessions by last_updated time
    const sessions = [...sessionsData.sessions].sort((a, b) => {
      const timeA = new Date(a.last_updated || a.created_time).getTime();
      const timeB = new Date(b.last_updated || b.created_time).getTime();

      // Default to newest first (descending order)
      if (filters.sortOrder === 'oldest') {
        return timeA - timeB; // Ascending order (oldest first)
      }
      return timeB - timeA; // Descending order (newest first)
    });

    return sessions;
  };

  // Scroll to top of the chat container
  const scrollToTop = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to bottom of the chat container
  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };


  // Handle type change (AI Agent <-> Human Support)
  const handleTypeChange = async (newType) => {
    if (!selectedSessionMetadata) return;

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      const url = `${apiUrl}/api/v1/chatbot/update-session-type`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatbot_id: agentId,
          session_id: selectedSessionMetadata.session_id,
          new_type: newType
        })
      });

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.errorUpdateSessionType'));
      }

      // Update local state
      setSelectedSessionMetadata(prev => ({
        ...prev,
        type: newType
      }));

      // Update in sessions data
      setSessionsData(prev => ({
        ...prev,
        sessions: prev.sessions.map(session =>
          session.session_id === selectedSessionMetadata.session_id
            ? { ...session, type: newType }
            : session
        )
      }));

      // Auto-switch to the correct tab in left sidebar
      // The useEffect watching filters will automatically trigger fetchSessions with correct filter
      setFilters(prev => ({
        ...prev,
        type: newType,
        page: 1 // Reset to first page
      }));

    } catch (err) {
      alert(err.message || 'Failed to update session type');
    }
  };

  // Handle delete session - show popup
  const handleDeleteSession = (sessionId, event) => {
    // Stop event propagation to prevent session selection
    if (event) {
      event.stopPropagation();
    }

    setDeletePopup({ isOpen: true, sessionId: sessionId });
  };

  // Confirm delete session - actual deletion
  const confirmDeleteSession = async () => {
    const sessionId = deletePopup.sessionId;
    setDeletePopup({ isOpen: false, sessionId: null });

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      if (!brandvoxToken) {
        throw new Error(t('dashboard.agentchats.errorAuthRequired'));
      }

      const url = `${apiUrl}/api/v1/chatbot/delete-session`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatbot_id: agentId,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error(t('dashboard.agentchats.deleteSessionError'));
      }

      // Remove from local state immediately
      setSessionsData(prev => ({
        ...prev,
        sessions: prev.sessions.filter(s => s.session_id !== sessionId),
        total_count: Math.max(0, prev.total_count - 1)
      }));

      // Clear selection if deleted session was selected
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setSelectedSessionMetadata(null);
        setCurrentSessionChats([]);
      }

      // If we deleted the last session on the current page and there are previous pages, go back one page
      const remainingSessions = sessionsData.sessions.filter(s => s.session_id !== sessionId);
      if (remainingSessions.length === 0 && filters.page > 1) {
        handlePageChange(filters.page - 1);
      }

    } catch (err) {
      setError(err.message || t('dashboard.agentchats.deleteSessionError'));
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle edit for Q&A
  const handleEditQA = (message) => {
    setQaData({
      title: '',
      question: message.question || '',
      answer: message.answer || ''
    });
    setShowQAModal(true);
  };


  // Handle QA training success
  const handleQATrainingSuccess = () => {
    navigate(`/agent/${agentId}/training`);
  };

  // Tour handlers
  const handleTourComplete = () => {
    localStorage.setItem(`human_support_tour_completed_${agentId}`, 'true');
    setShowTour(false);
  };

  const handleTourClose = () => {
    localStorage.setItem(`human_support_tour_completed_${agentId}`, 'true');
    setShowTour(false);
  };

  const handleRestartTour = () => {
    setShowTour(true);
  };

  // Render the chat messages for the selected conversation
  const renderSelectedChat = () => {
    if (!selectedSession || !selectedSessionMetadata) {
      return (
        <div className="no-chat-selected">
          <div className="no-chat-icon">
            <FiMessageSquare size={32} />
          </div>
          <h5>{t('dashboard.agentchats.noChatSelected')}</h5>
          <p>{t('dashboard.agentchats.selectConversation')}</p>
        </div>
      );
    }

    const conversations = currentSessionChats;

    // Get metadata from session metadata (not from first chat)
    const firstChat = conversations[0] || {};

    // Get feedback stats from session metadata
    const likeStats = selectedSessionMetadata?.feedback || { liked: 0, disliked: 0, none: 0 };

    // Group conversations by date
    const groupedByDate = conversations.reduce((groups, chat) => {
      const date = new Date(chat.timestamp).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(chat);
      return groups;
    }, {});

    // Sort dates from oldest to newest
    const sortedDates = Object.keys(groupedByDate).sort((a, b) =>
      new Date(a) - new Date(b)
    );

    return (
      <div className="selected-chat">
        <div className="chat-content-with-details">
          {/* Main chat area (messages + input) */}
          <div className="chat-main-area" ref={chatMainContentRef}>
            <div
              key={selectedSession}
              className="chat-messages-container"
              ref={chatContainerRef}
            >
              {sortedDates.map((date) => (
                <div key={date} className="chat-conversation-group">
                  <div className="chat-timestamp">
                    <span>{date}</span>
                  </div>
                  {groupedByDate[date].map((chat, index) => (
                    <React.Fragment key={index}>
                      {chat.question && chat.question.trim() !== '' && (
                        <ChatMessage
                          message={chat}
                          type="question"
                          onEdit={handleEditQA}
                          chatbotName={chatbotName}
                          selectedSessionMetadata={selectedSessionMetadata}
                        />
                      )}
                      {chat.answer && chat.answer.trim() !== '' && (
                        <ChatMessage
                          message={chat}
                          type="answer"
                          onEdit={handleEditQA}
                          chatbotName={chatbotName}
                          selectedSessionMetadata={selectedSessionMetadata}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>

            {/* Floating scroll buttons */}
            <div className="chat-scroll-buttons">
              <div
                className="scroll-btn"
                onClick={scrollToTop}
                title={t('dashboard.agentchats.scrollToTop')}
              >
                <FiArrowUp />
              </div>
              <div
                className="scroll-btn"
                onClick={scrollToBottom}
                title={t('dashboard.agentchats.scrollToBottom')}
              >
                <FiArrowDown />
              </div>
            </div>

            {/* Human Support Input Area */}
            <div className="human-support-input-container">
              {/* Agent Name Input */}
              <div className="agent-name-input-section" style={{
                padding: '0 8px 12px',
                borderBottom: '1px solid #e9ecef',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#495057',
                  whiteSpace: 'nowrap',
                  margin: 0
                }}>
                  {t('dashboard.agentchats.yourNameLabel')}
                </label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder={t('dashboard.agentchats.enterYourName')}
                  value={supportAgentName}
                  onChange={(e) => setSupportAgentName(e.target.value)}
                  onBlur={async (e) => {
                    const name = e.target.value.trim();
                    if (name) {
                      await saveAgentName(name);
                    }
                  }}
                  style={{
                    fontSize: '13px',
                    flex: 1
                  }}
                />
              </div>

              <div className="human-support-input-wrapper">
                {/* Markdown editor */}
                <div className="human-support-editor-container">
                  <CompactMDXEditor
                    key={editorKey}
                    value={humanMessage}
                    onChange={(value) => setHumanMessage(value)}
                    placeholder={t('dashboard.agentchats.typeYourResponse')}
                  />
                </div>

                {/* Send button */}
                <div className="human-support-send-container">
                  <PrimaryButton
                    onClick={sendHumanMessage}
                    disabled={!humanMessage.trim() || sendingMessage}
                    width="100%"
                  >
                    {sendingMessage ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend className="me-2" />
                        Send Reply
                      </>
                    )}
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </div>

          {/* Details Sidebar */}
          <div className="chat-details-sidebar">
            {/* Session Type Toggle - At the top */}
            {selectedSessionMetadata && (
              <div className="session-type-section">
                <div className="session-type-info">
                  <FiInfo className="info-icon" />
                  <p className="info-text">
                    Switch between AI automated responses and human agent handling for this session
                  </p>
                </div>
                <div className="session-type-toggle">
                  <button
                    className={`type-toggle-btn ${selectedSessionMetadata.type === 'AiAgent' ? 'active' : ''}`}
                    onClick={async () => {
                      if (selectedSessionMetadata.type !== 'AiAgent') {
                        await handleTypeChange('AiAgent');
                      }
                    }}
                  >
                    <FiCpu className="type-icon" />
                    <span>AI</span>
                  </button>
                  <button
                    className={`type-toggle-btn ${selectedSessionMetadata.type === 'HumanSupport' ? 'active' : ''}`}
                    onClick={async () => {
                      if (selectedSessionMetadata.type !== 'HumanSupport') {
                        await handleTypeChange('HumanSupport');
                      }
                    }}
                  >
                    <FiUser className="type-icon" />
                    <span>{t('dashboard.agentchats.humanLabel')}</span>
                  </button>
                </div>
              </div>
            )}

            <div className="details-header">
              {/* Desktop view */}
              <h6 className="d-none d-md-block">{t('dashboard.agentchats.detailsLabel')}</h6>

              {/* Mobile view - Avatar + Session ID */}
              <div className="d-md-none mobile-session-info">
                <div className="session-avatar">
                  <FiUser size={16} />
                </div>
                <span className="session-id-mobile">
                  {selectedSession?.substring(selectedSession.length - 8) || ''}
                </span>
              </div>
              <div className="details-header-actions">
                <LinkButton
                  variant="link"
                  className="d-md-none"
                  onClick={handleDetailsToggle}
                  aria-label={showDetails ? t('dashboard.agentchats.collapseDetails') : t('dashboard.agentchats.expandDetails')}
                >
                  {showDetails ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                </LinkButton>
                <LinkButton
                  onClick={(e) => handleDeleteSession(selectedSession, e)}
                  title={t('dashboard.agentchats.deleteSessionButton')}
                  aria-label={t('dashboard.agentchats.deleteSessionButton')}
                  className="text-danger"
                >
                  <FiTrash2 size={18} />
                </LinkButton>
                <ExportDropdown
                  data={conversations.map(chat => ({
                    date: new Date(chat.timestamp).toLocaleDateString(),
                    question: chat.question,
                    answer: chat.answer,
                    model: chat.model || t('dashboard.agentchats.notAvailable'),
                    country: chat.country || t('dashboard.agentchats.notAvailable'),
                    city: chat.city || t('dashboard.agentchats.notAvailable'),
                    source: chat.message_source || t('dashboard.agentchats.notAvailable'),
                    liked: chat.liked === "liked" ? t('dashboard.agentchats.filterLiked') : chat.liked === "disliked" ? t('dashboard.agentchats.filterDisliked') : "None",
                    user_ip: chat.user_ip || t('dashboard.agentchats.notAvailable'),
                    user_name: chat.user_display_name || t('dashboard.agentchats.notAvailable'),
                    user_email: chat.lead_info?.email || t('dashboard.agentchats.notAvailable'),
                    user_phone: chat.lead_info?.phone || t('dashboard.agentchats.notAvailable'),
                    timestamp: chat.timestamp,
                    lead_info: chat.lead_info,
                    user_display_name: chat.user_display_name,
                    message_source: chat.message_source,
                    agent_display_name: chat.agent_display_name,
                    human_support_email: chat.human_support_email
                  }))}
                  filename={`chat-${selectedSession.substring(selectedSession.length - 8)}`}
                  appliedFilters={{
                    session_id: selectedSession,
                    agent_name: chatbotName,
                    start_date: firstChat.timestamp ? new Date(firstChat.timestamp).toLocaleDateString() : null,
                    end_date: conversations.length > 0 ? new Date(conversations[conversations.length - 1].timestamp).toLocaleDateString() : null
                  }}
                  agentName={chatbotName}
                  sessionMetadata={selectedSessionMetadata}
                  isLoading={false}
                />
              </div>
            </div>

            <div className={`details-content ${showDetails ? 'show' : ''}`}>
              {firstChat.user_display_name && (
                <div className="detail-section">
                  <label className="detail-label">{t('dashboard.agentchats.nameLabel')}</label>
                  <div className="detail-value">
                    <span>{firstChat.user_display_name}</span>
                  </div>
                </div>
              )}

              {firstChat.message_source && (
                <div className="detail-section">
                  <label className="detail-label">{t('dashboard.agentchats.platformLabel')}</label>
                  <div className="detail-value">
                    <div className="platform-indicator">
                      <span className={`platform-icon platform-${firstChat.message_source}`}>
                        {firstChat.message_source === 'web' && <FiGlobe size={16} />}
                        {firstChat.message_source === 'whatsapp' && <BsWhatsapp size={16} />}
                        {firstChat.message_source === 'instagram' && <FiInstagram size={16} />}
                        {firstChat.message_source === 'messenger' && <FaFacebookMessenger size={16} />}
                        {firstChat.message_source === 'telegram' && <BsTelegram size={16} />}
                        {firstChat.message_source === 'slack' && <BsSlack size={16} />}
                      </span>
                      <span className="platform-name">
                        {firstChat.message_source === 'web' && t('dashboard.agentchats.sourceWebsite')}
                        {firstChat.message_source === 'whatsapp' && t('dashboard.agentchats.sourceWhatsApp')}
                        {firstChat.message_source === 'instagram' && t('dashboard.agentchats.sourceInstagram')}
                        {firstChat.message_source === 'messenger' && t('dashboard.agentchats.sourceMessenger')}
                        {firstChat.message_source === 'telegram' && t('dashboard.agentchats.sourceTelegram')}
                        {firstChat.message_source === 'slack' && t('dashboard.agentchats.sourceSlack')}
                        {!['web', 'whatsapp', 'instagram', 'messenger', 'telegram', 'slack'].includes(firstChat.message_source) && firstChat.message_source}
                      </span>
                    </div>
                  </div>
                </div>
              )}



              {selectedSessionMetadata?.lead_info && (
                <div className="detail-section lead-info-section">
                  <label className="detail-label">{t('dashboard.agentchats.leadInformationLabel')}</label>
                  <div className="detail-value lead-info-content">
                    {selectedSessionMetadata.lead_info.fullName && (
                      <div className="lead-info-item">
                        <span className="lead-info-label">
                          <FiUser className="detail-icon" /> {t('dashboard.agentchats.leadNameLabel')}
                        </span>
                        <span className="lead-info-text">{selectedSessionMetadata.lead_info.fullName}</span>
                      </div>
                    )}
                    {selectedSessionMetadata.lead_info.email && (
                      <div className="lead-info-item">
                        <span className="lead-info-label">
                          <FiMail className="detail-icon" /> {t('dashboard.agentchats.leadEmailLabel')}
                        </span>
                        <span className="lead-info-text">{selectedSessionMetadata.lead_info.email}</span>
                      </div>
                    )}
                    {selectedSessionMetadata.lead_info.phone && (
                      <div className="lead-info-item">
                        <span className="lead-info-label">
                          <FiPhone className="detail-icon" /> {t('dashboard.agentchats.leadPhoneLabel')}
                        </span>
                        <span className="lead-info-text">{selectedSessionMetadata.lead_info.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(firstChat.country || firstChat.city !== t('dashboard.agentchats.modelUnknown')) && (
                <div className="detail-section">
                  <label className="detail-label">{t('dashboard.agentchats.location')}</label>
                  <div className="detail-value">
                    <FiMapPin className="detail-icon" />
                    <span>{firstChat.country}{firstChat.city !== t('dashboard.agentchats.modelUnknown') ? `, ${firstChat.city}` : ''}</span>
                  </div>
                </div>
              )}

              {selectedSessionMetadata?.ip_address && (
                <div className="detail-section">
                  <label className="detail-label">{t('dashboard.agentchats.ipAddressLabel')}</label>
                  <div className="detail-value">
                    <span className="ip-address">{selectedSessionMetadata.ip_address}</span>
                  </div>
                </div>
              )}

              <div className="detail-section">
                <label className="detail-label">{t('dashboard.agentchats.sessionLabel')}</label>
                <div className="detail-value">
                  <span className="session-id">...{selectedSessionMetadata?.session_id?.substring(selectedSessionMetadata.session_id.length - 12)}</span>
                </div>
              </div>

              <div className="detail-section">
                <label className="detail-label">{t('dashboard.agentchats.startedLabel')}</label>
                <div className="detail-value">
                  <span>{selectedSessionMetadata?.created_time ? new Date(selectedSessionMetadata.created_time).toLocaleString() : t('dashboard.agentchats.notAvailable')}</span>
                </div>
              </div>

              <div className="detail-section">
                <label className="detail-label">{t('dashboard.agentchats.lastActivity')}</label>
                <div className="detail-value">
                  <span>{selectedSessionMetadata?.last_updated ? new Date(selectedSessionMetadata.last_updated).toLocaleString() : t('dashboard.agentchats.notAvailable')}</span>
                </div>
              </div>

              <div className="detail-section">
                <label className="detail-label">{t('dashboard.agentchats.messagesLabel')}</label>
                <div className="detail-value">
                  <span>{selectedSessionMetadata?.message_count || conversations.length}</span>
                </div>
              </div>

              {(likeStats.liked > 0 || likeStats.disliked > 0) && (
                <div className="detail-section">
                  <label className="detail-label">{t('dashboard.agentchats.feedbackLabel')}</label>
                  <div className="detail-value feedback-stats">
                    {likeStats.liked > 0 && (
                      <span className="feedback-stat liked">
                        <FiThumbsUp className="detail-icon" />
                        {likeStats.liked}
                      </span>
                    )}
                    {likeStats.disliked > 0 && (
                      <span className="feedback-stat disliked">
                        <FiThumbsDown className="detail-icon" />
                        {likeStats.disliked}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* QA Edit Modal */}
        <AddQAModal
          show={showQAModal}
          onHide={() => setShowQAModal(false)}
          agentId={agentId}
          apiUrl={apiUrl}
          onPollState={handleQATrainingSuccess}
          initialData={qaData}
        />
      </div>
    );
  };

  // Add pagination component
  const PaginationControls = () => {
    // Don't render if there's only 1 page or no data
    if (!sessionsData || !sessionsData.total_pages || sessionsData.total_pages <= 1) return null;

    const currentPage = sessionsData.current_page || 1;
    const totalPages = sessionsData.total_pages || 1;

    // Create page items
    let items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      />
    );

    // First page
    items.push(
      <Pagination.Item
        key={1}
        active={1 === currentPage}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );

    // Ellipsis if needed
    if (currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    // Pages around current
    for (let page = Math.max(2, currentPage - 1); page <= Math.min(totalPages - 1, currentPage + 1); page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Ellipsis if needed
    if (currentPage < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }

    // Last page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {sessionsData.sessions.length} conversations - Page {currentPage} of {totalPages}
        </div>
        <Pagination className="pagination-controls">{items}</Pagination>
      </div>
    );
  };

  // Render chat sidebar with improved mobile handling
  const renderChatSidebar = () => {
    const hasChats = getSortedSessions().length > 0;

    return (
      <div className="chat-sidebar">
        <div className="sidebar-header-chats">
          <h5>{t('dashboard.agentchats.conversationsLabel')}</h5>
          <LinkButton
            size="sm"
            className="filter-toggle-btn"
            onClick={handleFilterToggle}
          >
            {showFilters ?
              <><FiEyeOff className="me-1" /> {t('dashboard.agentchats.hideFilters')}</> :
              <><FiFilter className="me-1" /> {t('dashboard.agentchats.showFilters')}</>
            }
          </LinkButton>
        </div>

        {/* Filters section with improved scrolling */}
        <div className={`sidebar-filters ${showFilters ? 'd-block' : 'd-none'}`}>
          <div className="filter-section">
            {workspaceData && workspaceData.workspace_bv_chat_history_day_limit < 10000 && (
              <div className="w-100 mb-2">
                <CompactUpgradePrompt
                  message={
                    t('dashboard.agentchats.upgradeChatHistoryMessage', { days: workspaceData.workspace_bv_chat_history_day_limit })
                  }
                  buttonText={t('dashboard.agentchats.upgradeButton')}
                  percentage={90}
                  type="quota"
                />
              </div>
            )}
            <div className="date-range-filters">
              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterStartDate')}</Form.Label>
                <div className="date-picker-container">
                  <DateTimePicker
                    placeholder={t('dashboard.agentchats.selectStartDatePlaceholder')}
                    value={currentFilters.start_date}
                    onChange={(date) => handleDateChange('start_date', date)}
                    clearable
                    size="sm"
                    valueFormat="DD/MM/YYYY HH:mm"
                    aria-label={t('dashboard.agentchats.filterStartDate')}
                    monthsListFormat="MMM"
                    monthFormat="MMMM YYYY"
                    firstDayOfWeek={0}
                    weekdayFormat="dd"
                    minDate={getMinDate()}
                    styles={{
                      root: { width: '100%' },
                      input: { fontSize: rem(14) }
                    }}
                  />
                </div>
              </div>
              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterEndDate')}</Form.Label>
                <div className="date-picker-container">
                  <DateTimePicker
                    placeholder={t('dashboard.agentchats.selectEndDatePlaceholder')}
                    value={currentFilters.end_date}
                    onChange={(date) => handleDateChange('end_date', date)}
                    clearable
                    size="sm"
                    valueFormat="DD/MM/YYYY HH:mm"
                    aria-label={t('dashboard.agentchats.filterEndDate')}
                    monthsListFormat="MMM"
                    monthFormat="MMMM YYYY"
                    firstDayOfWeek={0}
                    weekdayFormat="dd"
                    minDate={getMinDate()}
                    styles={{
                      root: { width: '100%' },
                      input: { fontSize: rem(14) }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="filter-group">
              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterSource')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.source}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                >
                  {filterOptions.message_sources.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterModel')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                >
                  {filterOptions.models.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <div className="filter-group">
              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterCountry')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.country}
                  onChange={(e) => handleFilterChange('country', e.target.value)}
                >
                  {filterOptions.countries.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterCity')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                >
                  {filterOptions.cities.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>


            <div className="filter-group">
              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterFeedback')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.likeStatus}
                  onChange={(e) => handleFilterChange('likeStatus', e.target.value)}
                >
                  {likeStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="filter-item">
                <Form.Label className="filter-label">{t('dashboard.agentchats.filterSortOrder')}</Form.Label>
                <Form.Select
                  className="form-select-custom"
                  value={currentFilters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>

            <div className="filter-actions">
              <OutlinedSecondaryButton
                width="100%"
                onClick={resetFilters}
              >
                <FiRefreshCw className="me-1" />
                {t('dashboard.agentchats.resetFiltersButton')}
              </OutlinedSecondaryButton>
              <PrimaryButton
                width="100%"
                onClick={applyFilters}
              >
                <FiSearch className="me-1" /> {t('dashboard.agentchats.searchButton')}
              </PrimaryButton>
            </div>
          </div>
        </div>

        {/* Type filter tabs */}
        <div className="session-type-tabs">
          {typeOptions.map(option => (
            <button
              key={option.value}
              className={`type-tab ${filters.type === option.value ? 'active' : ''}`}
              onClick={() => {
                const newFilters = {
                  ...filters,
                  type: option.value,
                  page: 1
                };
                setFilters(newFilters);
                setCurrentFilters(newFilters);
                setFiltersApplied(true);
              }}
            >
              {option.value === 'all' && <FiMessageSquare className="tab-icon" />}
              {option.value === 'AiAgent' && <FiCpu className="tab-icon" />}
              {option.value === 'HumanSupport' && <FiUser className="tab-icon" />}
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* Chat list */}
        <div className="sidebar-chats-list">
          {loading ? (
            <LoadingMessage message={t('dashboard.agentchats.loadingChats')} />
          ) : error ? (
            <Alert variant="danger" className="m-3">
              <Alert.Heading>{t('dashboard.agentchats.errorHeading')}</Alert.Heading>
              <p>{error}</p>
              <OutlinedDangerButton
                size="sm"
                onClick={fetchSessions}
              >
                {t('dashboard.agentchats.retryButton')}
              </OutlinedDangerButton>
            </Alert>
          ) : !hasChats ? (
            <div className="empty-sidebar">
              <FiInfo size={36} />
              <p>{t('dashboard.agentchats.noConversationsFound')}</p>
              <PrimaryButton
                size="sm"
                onClick={applyFilters}
                className="mt-2"
              >
                <FiRefreshCw style={{ marginBottom: '2px', marginRight: '0.5rem', color: 'white' }} />
                {t('dashboard.agentchats.refreshButton')}
              </PrimaryButton>
            </div>
          ) : (
            <>
              {getSortedSessions().map(sessionMetadata => {
                return (
                  <ChatSidebarItem
                    key={sessionMetadata.session_id}
                    sessionMetadata={sessionMetadata}
                    isActive={selectedSession === sessionMetadata.session_id}
                    onClick={() => handleChatSelect(sessionMetadata)}
                    onDelete={handleDeleteSession}
                  />
                );
              })}
              <PaginationControls />
            </>
          )}
        </div>
      </div>
    );
  };

  // Empty state when no chats found
  const renderEmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-icon">
        <FiMessageSquare size={48} />
      </div>
      <h4>{t('dashboard.agentchats.noConversationsFoundTitle')}</h4>
      <p>{t('dashboard.agentchats.noConversationsFound')}</p>
      <div className="d-flex gap-2 mt-3">
        <OutlinedSecondaryButton
          onClick={resetFiltersWithFetch}
        >
          <FiFilter className="me-2" />
          {t('dashboard.agentchats.resetFilters')}
        </OutlinedSecondaryButton>
        <PrimaryButton
          onClick={fetchSessions}
        >
          <FiRefreshCw className="me-2" />
          {t('dashboard.agentchats.refreshButton')}
        </PrimaryButton>
      </div>
    </div>
  );


  // Track if user has scrolled to enhance mobile UX
  useEffect(() => {
    if (!chatContainerRef.current) return;

    const chatContainer = chatContainerRef.current;
    let hasScrolled = false;

    const handleScroll = () => {
      if (!hasScrolled) {
        hasScrolled = true;

        // Also hide the instruction text if visible
        const scrollInstruction = chatContainer.querySelector('.chat-messages-container::before');
        if (scrollInstruction) {
          scrollInstruction.style.opacity = '0';
        }
      }
    };

    chatContainer.addEventListener('scroll', handleScroll);

    return () => {
      chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [selectedSession]);

  // Main render
  return (
    <div className="agent-chats-container">
      <PageHeader
        icon={<FiMessageSquare />}
        title={t('dashboard.agentchats.conversationsTitle')}
        description={t('dashboard.agentchats.conversationsDescription')}
        hideMobile={true}
        secondaryButton={{
          onClick: handleRestartTour,
          className: 'header-tour-btn',
          children: (
            <>
              <FiInfo size={16} className="me-2" />
              {t('dashboard.agentchats.viewTutorial')}
            </>
          )
        }}
      />

      {loading ? (
        <AgentChatsSkeleton />
      ) : error ? (
        <Alert variant="danger" className="m-3">
          <Alert.Heading>{t('dashboard.agentchats.errorHeading')}</Alert.Heading>
          <p>{error}</p>
          <OutlinedDangerButton
            onClick={fetchSessions}
          >
            {t('dashboard.agentchats.retryButton')}
          </OutlinedDangerButton>
        </Alert>
      ) : (
        <div className="chats-content">
          {renderChatSidebar()}

          <div className="chat-main-content">
            {sessionsData.sessions && sessionsData.sessions.length > 0
              ? renderSelectedChat()
              : renderEmptyState()
            }
          </div>
        </div>
      )}

      {/* Delete Session Confirmation Popup */}
      <CustomPopup
        isOpen={deletePopup.isOpen}
        onClose={() => setDeletePopup({ isOpen: false, sessionId: null })}
        onConfirm={confirmDeleteSession}
        title={t('dashboard.agentchats.deleteSessionTitle')}
        message={t('dashboard.agentchats.deleteSessionMessage')}
        type="confirm"
        confirmText={t('dashboard.agentchats.deleteSessionConfirm')}
        cancelText={t('dashboard.agentchats.cancelButton')}
        showCancel={true}
      />

      {/* Human Support Tour */}
      <HumanSupportTour
        show={showTour}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
        chatbotId={agentId}
      />
    </div>
  );
};

export default AgentChats;